import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export function PageTransition({ children, className }: PageTransitionProps) {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(children);
  const prevLocation = useRef(location.pathname);

  useEffect(() => {
    // If location changed, trigger exit animation then update children
    if (prevLocation.current !== location.pathname) {
      setIsVisible(false);
      
      const timeout = setTimeout(() => {
        setDisplayChildren(children);
        prevLocation.current = location.pathname;
        // Small delay before enter animation
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }, 150);

      return () => clearTimeout(timeout);
    } else {
      // Initial mount
      setDisplayChildren(children);
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }
  }, [location.pathname, children]);

  return (
    <div
      className={cn(
        "transition-all duration-200 ease-out",
        isVisible 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-2",
        className
      )}
    >
      {displayChildren}
    </div>
  );
}

// Simpler version for components that don't need route-based transitions
export function FadeIn({ 
  children, 
  delay = 0,
  className 
}: { 
  children: React.ReactNode; 
  delay?: number;
  className?: string;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [delay]);

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-out",
        isVisible 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-4",
        className
      )}
    >
      {children}
    </div>
  );
}

// Staggered list animation
export function StaggeredList({ 
  children, 
  staggerDelay = 50,
  className 
}: { 
  children: React.ReactNode[];
  staggerDelay?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      {children.map((child, index) => (
        <FadeIn key={`stagger-${index}`} delay={index * staggerDelay}>
          {child}
        </FadeIn>
      ))}
    </div>
  );
}
